/**
 * BulkDiscountConfirmDialog
 *
 * Modal confirmation shown when the user applies a bulk discount from the
 * merged table's toolbar with no rows selected, so the discount targets
 * EVERY unit in the offer. Moved here from the retired Wyceny tab
 * (`offer-unified-table` capability, CU-86cbc3r8b) — its "nothing selected ->
 * whole offer" targeting is unchanged, but because the merged table can now
 * be filtered while the retired pricing surface could not, the dialog must
 * additionally say so when a filter is active: the count alone no longer
 * tells a filtered-view user that their filter is being ignored.
 */
import * as React from 'react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import type { TFunction } from 'i18next';

const DIALOG_MAX_WIDTH = 'max-w-sm';

// ─── Props ────────────────────────────────────────────────────

export interface BulkDiscountConfirmDialogProps {
  /** Total number of rows that will be affected — the offer's true total, not the filtered/loaded count. */
  rowCount: number;
  /** Discount percent that will be applied. */
  discountPercent: number;
  /** Number of rows that already have a non-zero discount set (will be overwritten). */
  rowsWithExistingDiscount: number;
  /** True when a filter is currently narrowing the table's view — the discount reaches every unit regardless. */
  isFiltered: boolean;
  /**
   * True while the gated all-rows fetch backing the confirm action hasn't
   * resolved yet. Disables the Confirm button so a click can't silently
   * no-op.
   */
  disabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  t: TFunction<'offers'>;
}

// ─── Component ───────────────────────────────────────────────

export function BulkDiscountConfirmDialog({
  rowCount,
  discountPercent,
  rowsWithExistingDiscount,
  isFiltered,
  disabled = false,
  onConfirm,
  onCancel,
  t,
}: BulkDiscountConfirmDialogProps) {
  return (
    <ModalDialog
      header={
        <h2 className="text-base font-semibold">{t('workspace.pricingTab.confirmDialog.title')}</h2>
      }
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('workspace.pricingTab.confirmDialog.cancel')}
          </Button>
          <Button
            size="sm"
            data-testid="bulk-discount-confirm-all"
            disabled={disabled}
            onClick={onConfirm}
            aria-label={t('workspace.pricingTab.confirmDialog.confirmAriaLabel')}
          >
            {t('workspace.pricingTab.confirmDialog.confirm')}
          </Button>
        </>
      }
      onClose={onCancel}
      closeLabel={t('workspace.pricingTab.confirmDialog.closeAriaLabel')}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="bulk-discount-confirm-dialog"
    >
      <p className="text-sm text-foreground">
        {t('workspace.pricingTab.confirmDialog.description', {
          count: rowCount,
          discountPercent,
        })}
      </p>
      <p className="mt-2 text-sm font-semibold">
        {t('workspace.pricingTab.confirmDialog.rowCount', { count: rowCount })}
      </p>
      {isFiltered ? (
        <p className="mt-2 text-sm text-foreground" data-testid="bulk-discount-filter-notice">
          {t('workspace.pricingTab.confirmDialog.filterDoesNotNarrow')}
        </p>
      ) : null}
      {rowsWithExistingDiscount > 0 ? (
        <p className="mt-2 text-sm text-destructive" data-testid="bulk-discount-overwrite-warning">
          {t('workspace.pricingTab.confirmDialog.overwriteWarning', {
            count: rowsWithExistingDiscount,
          })}
        </p>
      ) : null}
      {disabled ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="bulk-discount-loading-rows">
          {t('workspace.pricingTab.confirmDialog.loadingRows')}
        </p>
      ) : null}
    </ModalDialog>
  );
}
